/**
 * SnsBtn — 카카오·네이버·구글 SNS 버튼 (로그인·회원가입 화면 공용)
 *
 * ui-core `Button` 으로 치환하지 않는다 — 배경·글자색이 D-07 §5.3 「외부 채널 브랜드색 예외」라
 * variant 체계 밖이다. 대신 **기하(높이·radius)만 `.btn-lg` 와 일치**시켜 같은 화면에 놓인
 * 주 버튼과 크기가 갈리지 않게 한다 (D-09 §15.1 웹 lg = 48 · radius-md).
 */
interface SnsBtnProps {
  onClick: () => void;
  isLoading: boolean;
  iconBg: string;
  iconColor: string;
  iconLabel: string;
  label: string;
  btnStyle: React.CSSProperties;
  labelColor: string;
  disabled: boolean;
}

export function SnsBtn({ onClick, isLoading, iconBg, iconColor, iconLabel, label, btnStyle, labelColor, disabled }: SnsBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-heading-sm font-bold"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--gap-sm)',
        width: '100%', height: 'var(--button-height-lg)', padding: '0 var(--spacing-05)',
        borderRadius: 'var(--radius-md)',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', marginBottom: 'var(--gap-sm)',
        transition: 'filter .15s, opacity .12s',
        color: labelColor,
        opacity: disabled ? 0.6 : 1,
        ...btnStyle,
      }}
    >
      {isLoading ? (
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20, flexShrink: 0 }}
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      ) : (
        <span
          aria-hidden="true"
          className="text-caption font-bold"
          style={{
            width: 22, height: 22, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
            background: iconBg, color: iconColor,
          }}
        >
          {iconLabel}
        </span>
      )}
      {isLoading ? '처리 중…' : label}
    </button>
  );
}

interface SnsBtnGroupProps {
  onClick: (provider: 'kakao' | 'naver' | 'google') => void;
  loadingProvider: 'kakao' | 'naver' | 'google' | null;
  anyLoading: boolean;
  /** 버튼 라벨 접미사 — 로그인 화면 "시작하기" / 회원가입 화면 "빠른 가입" */
  labelSuffix: string;
}

/*
 * 외부 채널 브랜드색 — D-07 §5.3 예외(각 사 브랜드 가이드 공식값). 값의 SSOT는 브랜드 토큰(--brand-*).
 * 임의로 바꾸면 각 사 브랜드 가이드 위반이 된다.
 */
const BRAND = {
  kakao:  { iconBg: 'var(--brand-kakao-symbol)',  iconColor: 'var(--brand-kakao-container)',  bg: 'var(--brand-kakao-container)',  label: 'var(--brand-kakao-label)' },
  naver:  { iconBg: 'var(--brand-naver-label)',   iconColor: 'var(--brand-naver-container)',  bg: 'var(--brand-naver-container)',  label: 'var(--brand-naver-label)' },
  google: { iconBg: 'var(--brand-google-symbol)', iconColor: 'var(--brand-google-container)', bg: 'var(--brand-google-container)', label: 'var(--brand-google-label)' },
} as const;

/** 카카오·네이버·구글 3버튼 세트 — 로그인/가입 양쪽에서 동일하게 사용 */
export function SnsBtnGroup({ onClick, loadingProvider, anyLoading, labelSuffix }: SnsBtnGroupProps) {
  return (
    <>
      <SnsBtn
        onClick={() => onClick('kakao')}
        isLoading={loadingProvider === 'kakao'}
        disabled={anyLoading}
        iconBg={BRAND.kakao.iconBg} iconColor={BRAND.kakao.iconColor} iconLabel="K"
        label={`카카오로 ${labelSuffix}`} labelColor={BRAND.kakao.label}
        btnStyle={{ background: BRAND.kakao.bg, boxShadow: 'var(--elevation-1)' }}
      />
      <SnsBtn
        onClick={() => onClick('naver')}
        isLoading={loadingProvider === 'naver'}
        disabled={anyLoading}
        iconBg={BRAND.naver.iconBg} iconColor={BRAND.naver.iconColor} iconLabel="N"
        label={`네이버로 ${labelSuffix}`} labelColor={BRAND.naver.label}
        btnStyle={{ background: BRAND.naver.bg, boxShadow: 'var(--elevation-1)' }}
      />
      <SnsBtn
        onClick={() => onClick('google')}
        isLoading={loadingProvider === 'google'}
        disabled={anyLoading}
        iconBg={BRAND.google.iconBg} iconColor={BRAND.google.iconColor} iconLabel="G"
        label={`구글로 ${labelSuffix}`} labelColor={BRAND.google.label}
        btnStyle={{ background: BRAND.google.bg, border: '1px solid var(--brand-google-border)', boxShadow: 'var(--elevation-1)' }}
      />
    </>
  );
}
